import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { usePractitioners } from 'api/queries/marketing-capabilities/practitioners/usePractitioners'
import { useSkills } from 'api/queries/marketing-capabilities/skills/useSkills'
import {
  setExploreLearningsLocation,
  setExploreLearningsOnboarding,
  setExploreLearningsOU,
  setExploreLearningsSkill,
  setExploreLearningsSkillNames,
  setExploreLearningsSort,
  useExploreLearnings,
} from 'store/ExploreLearnings'

import { CommunityFilters } from '../components/CommunityFilters'
import { EmptyResult } from '../components/EmptyResult'
import { PractitionerCard } from '../components/PractitionerCard'
import { PractitionerModal } from '../components/PractitionerModal'

export const CommunityPage = () => {
  const { data: practitionerList, isLoading, hasNextPage, fetchNextPage } = usePractitioners()
  const { data: skillList } = useSkills(true)
  const location = useLocation()

  const practitioners = !isLoading && practitionerList && practitionerList.pages.flat()
  const count = () => practitionerList?.meta.total || 0
  const search = useExploreLearnings(state => state.search)
  const ouState = useExploreLearnings(state => state.OU)
  const locationState = useExploreLearnings(state => state.location)
  const skillState = useExploreLearnings(state => state.skill)
  const onboardingState = useExploreLearnings(state => state.onboarding)

  const [selectedPractitioner, setSelectedPractitioner] = useState<any>(undefined)

  useEffect(() => {
    if (!skillList) return

    const params = new URLSearchParams(location.search)
    const skillsParam = params.get('skills')
    const onboardingParam = params.get('onboarding')
    const skillLabels = skillsParam ? skillsParam.split(',').map(s => s.trim()) : []

    if (skillLabels.length > 0) {
      const skillListData = Array.isArray(skillList) ? skillList : skillList?.data
      const validSkills = skillLabels
        .map(label => skillListData.find((skill: any) => skill.name === label))
        .filter((skill: any): skill is any => !!skill)

      const validSkillIds = validSkills.map((skill: any) => skill.id)
      const validSkillNames = validSkills.map((skill: any) => skill.name)

      if (validSkillIds.length > 0) {
        setExploreLearningsSkill(validSkillIds)
        setExploreLearningsSkillNames(validSkillNames)
      }
    }

    if (onboardingParam === 'true' && onboardingState !== 'true') {
      setExploreLearningsOnboarding('true')
    }
  }, [location.search, skillList, onboardingState])

  const handleApplyFilters = (ous: string[], locations: string[], skills: string[]) => {
    setExploreLearningsOU(ous)
    setExploreLearningsLocation(locations)
    setExploreLearningsSkill(skills)

    // Map skill IDs to skill names
    if (skills.length > 0 && skillList) {
      const skillListData = Array.isArray(skillList) ? skillList : skillList?.data
      const skillNames = skills
        .map(id => skillListData.find((skill: any) => skill.id === id)?.name)
        .filter((name: any): name is string => !!name)
      setExploreLearningsSkillNames(skillNames)
    } else {
      setExploreLearningsSkillNames([])
    }
  }

  const handleClearAllFilters = () => {
    setExploreLearningsOU([])
    setExploreLearningsLocation([])
    setExploreLearningsSkill([])
    setExploreLearningsSkillNames([])
    setExploreLearningsOnboarding('false')
  }
  return (
    <>
      {(!search || count() > 0) && (
        <CommunityFilters
          ouState={ouState}
          locationState={locationState}
          skillState={skillState}
          onApplyFilters={handleApplyFilters}
          onClearAllFilters={handleClearAllFilters}
          onSort={setExploreLearningsSort}
        />
      )}
      {search && count() > 0 ? (
        <div className="w-[1120px] mx-auto">
          <p className="text-[16px] leading-[24px] font-TCCCText mt-2 mb-[16px]">
            <b className="text-[20px] font-semibold leading-[32px]">{count()}</b> practitioner{count() !== 1 ? 's' : ''}{' '}
            matching with “{search}”
          </p>
        </div>
      ) : (
        ''
      )}
      {practitioners && practitioners.length > 0 && !isLoading ? (
        <div className="mx-auto flex flex-wrap pb-8 gap-x-[23px] gap-y-8 w-[1120px]">
          {practitioners.map((practitioner: any) => (
            <div className="cursor-pointer" onClick={() => setSelectedPractitioner(practitioner)} key={practitioner.id}>
              <PractitionerCard
                key={practitioner.id}
                name={`${practitioner.name} ${practitioner.surname}`}
                email={practitioner.email}
                imageUrl={practitioner.photoUrl}
                role={practitioner.jobPosition}
                location={practitioner.location}
                tags={practitioner.skills}
              />
            </div>
          ))}
        </div>
      ) : (
        ''
      )}
      {hasNextPage && count() > 0 && (
        <button className="w-full mx-auto text-blue-500 font-bold font-TCCC mb-16" onClick={() => fetchNextPage()}>
          See more
        </button>
      )}

      {selectedPractitioner && (
        <PractitionerModal
          selectedPractitioner={selectedPractitioner}
          onClose={() => setSelectedPractitioner(undefined)}
        />
      )}

      {count() === 0 && !isLoading && <EmptyResult />}

      {practitionerList &&
        practitionerList?.exploreMorePractitioners &&
        practitionerList?.exploreMorePractitioners.length > 0 &&
        !isLoading && (
          <div className="w-[1120px] pb-16 mx-auto flex flex-col border-t border-solid border-[#D7D7D7] pt-10">
            <div className="flex justify-between mb-7 items-center">
              <p className="heading-2xl">Explore practitioners</p>
            </div>
            <div className="flex flex-wrap gap-x-[23px] gap-y-8">
              {practitionerList?.exploreMorePractitioners.map((practitioner: any) => (
                <div
                  className="cursor-pointer"
                  onClick={() => setSelectedPractitioner(practitioner)}
                  key={practitioner.id}
                >
                  <PractitionerCard
                    key={practitioner.id}
                    name={`${practitioner.name} ${practitioner.surname}`}
                    email={practitioner.email}
                    imageUrl={practitioner.photoUrl}
                    role={practitioner.jobPosition}
                    location={practitioner.location}
                    tags={practitioner.skills}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
    </>
  )
}
